import React, { useState, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { withStyles } from "@mui/styles";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Card,
  CardActions,
  Box,
  Grid,
  CardMedia,
  TextField,
  Button,
  MenuItem,
  Typography,
  CircularProgress,
  CardContent,
  Alert,
  AlertTitle,
  ListItemText,
  Pagination,
} from "@mui/material";
import { useMessage } from "../../context/message-context";
import LoadingComponent from "../LoadingComponent/LoadingComponent";
import { fetchAblagenamen, uploadFile, fetchTransferHistory, fetchAufgabe, fetchFile, fetchQualificationKeys } from ".";
import TransferHistory from "../TransferHistoryComponent/TransferHistory";
import openFile from "../shared/functions/download";
import { isImage } from "../../functions/default";
import FileUploadButton from "../shared/components/PictureUploadComponent";

const styles = (theme) => ({
  root: {
    marginLeft: theme.spacing(9),
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    [theme.breakpoints.down("sm")]: {
      marginLeft: 0,
    },
  },
  paper: {
    margin: theme.spacing(2),
    padding: theme.spacing(2),
  },
  typo: {
    flexGrow: 1,
    overflow: "hidden",
  },
  sendButton: {
    marginLeft: "auto",
  },
});

function FileTransfer({ accessToken, client, location, classes }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { showErrorMessage, showSuccessMessage, hideMessage } = useMessage();
  const [ablagenamen, setAblagenamen] = useState([]);
  const [aufgabe, setAufgabe] = useState(null);
  const [history, setHistory] = useState({
    allKeysByDate: [],
    byKey: {},
  });
  const [qualificationKeys, setQualificationKeys] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [selectedAblagename, setSelectedAblagename] = useState("");

  const [selectedFile, setSelectedFile] = useState(null);
  const [showFileNumber, setShowFileNumber] = useState(0);
  const [gueltigAb, setGueltigAb] = useState(moment(new Date()).format("YYYY-MM-DD"));
  const [gueltigBis, setGueltigBis] = useState("0000-00-00");
  const [fieldErrors, setFieldErrors] = useState({
    ablagename: "",
    gueltigAb: "",
    gueltigBis: "",
  });

  const hvktKey = searchParams.get("hvktKey") ? searchParams.get("hvktKey") : "";
  const haufKey = searchParams.get("haufKey") ? searchParams.get("haufKey") : "";
  const aufgabenKey = hvktKey !== "" ? hvktKey : haufKey !== "" ? haufKey : "";
  const gueltigAbAnzeigen = useMemo(() => {
    if (aufgabe && aufgabe.DB === "LHRZ") return true;
    return qualificationKeys.includes(selectedAblagename);
  }, [ablagenamen, selectedAblagename, qualificationKeys]);
  const gueltigBisAnzeigen = aufgabe && aufgabe.DB === "LHRZ" && aufgabe.LHRE_INDIVIDUELLER_TURNUS;

  const ablagenameSelectOptions = useMemo(
    () => (!!aufgabe && !!aufgabe.HDOK_KEY ? [{ key: aufgabe.HDOK_KEY, name: aufgabe.HDOK_NAME }] : [...ablagenamen]),
    [aufgabe, ablagenamen],
  );

  useEffect(() => {
    setIsLoading(true);
    fetchAblagenamen(client, accessToken, aufgabenKey)
      .then((neueAblagenamen) => {
        if (neueAblagenamen) setAblagenamen(neueAblagenamen);
      })
      .catch((err) => {
        showErrorMessage(err);
      })
      .finally(() => {
        setIsLoading(false);
      });

    fetchTransferHistory(client, accessToken, aufgabenKey)
      .then((newHistory) => {
        if (newHistory) setHistory(newHistory);
      })
      .catch((err) => {
        showErrorMessage(err);
      })
      .finally(() => {
        setIsLoading(false);
      });

    fetchQualificationKeys(client, accessToken, aufgabenKey)
      .then((newQualifications) => {
        if (newQualifications) setQualificationKeys(newQualifications);
      })
      .catch((err) => {
        showErrorMessage(err);
      })
      .finally(() => {
        setIsLoading(false);
      });

    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    setIsLoading(true);
    if (aufgabenKey) {
      fetchAufgabe(client, accessToken, aufgabenKey)
        .then((a) => {
          setAufgabe(a);
          if (a && a.HDOK_KEY) {
            setSelectedAblagename(a.HDOK_KEY);
          }
        })
        .catch((err) => {
          showErrorMessage(err);
        });
    } else {
      setAufgabe(null);
    }
    // eslint-disable-next-line
  }, [aufgabenKey]);

  const getDataUrl = (img) => {
    // Create canvas
    return window.URL.createObjectURL(img);
  };

  const handleAblagenameChange = (e) => {
    setSelectedAblagename(e.target.value);
    setFieldErrors({ ...fieldErrors, ablagename: "" });
  };

  const handleGueltigAbChange = (e) => {
    setGueltigAb(e.target.value);
    setFieldErrors({ ...fieldErrors, gueltigAb: "" });
  };

  const handleGueltigBisChange = (e) => {
    setGueltigBis(e.target.value);
    setFieldErrors({ ...fieldErrors, gueltigBis: "" });
  };

  const onDateiChange = (e) => {
    let name = e.target.files[0].name.split(".");

    if ("jpeg;png;pdf;jpg;heic;xlsx;PDF;".includes(name[name.length - 1] + ";")) {
      setShowFileNumber(0);
      setSelectedFile([e.target.files[0]]);
    } else {
      showErrorMessage(t("Falsches Dateiformat"));
    }
  };
  const addDatei = (e) => {
    let name = e.target.files[0].name.split(".");
    if ("jpeg;png;pdf;jpg;heic;xlsx;PDF;".includes(name[name.length - 1] + ";")) {
      setSelectedFile((prevFiles) => {
        if (Array.isArray(prevFiles)) {
          return [...prevFiles, e.target.files[0]];
        } else {
          return [prevFiles, e.target.files[0]];
        }
      });
    } else {
      showErrorMessage(t("Falsches Dateiformat"));
    }
  };
  const onSubmit = () => {
    if (isSending) return;
    if (!selectedAblagename) {
      setFieldErrors({
        ...fieldErrors,
        ablagename: t("Bitte wählen Sie die Art des Dokuments aus."),
      });
      return;
    } else {
      setFieldErrors({ ...fieldErrors, ablagename: "" });
    }
    if (!selectedFile) {
      showErrorMessage(t("Keine Datei ausgewählt"));
      return;
    }

    hideMessage();
    setIsSending(true);

    uploadFile(client, accessToken, selectedFile, selectedAblagename, aufgabenKey, gueltigAb, gueltigBis)
      .then((response) => {
        if (response) {
          const newHistory = { ...history };
          if (!newHistory.byKey) {
            newHistory.byKey = {};
          }
          newHistory.allKeysByDate = [response.key, ...newHistory.allKeysByDate];
          newHistory.byKey[response.key] = { ...response };
          setHistory(newHistory);
          showSuccessMessage(t("Upload erfolgreich."));
          if (aufgabe) {
            navigate(-1);
          } else {
            setSelectedAblagename("");
            setSelectedFile(null);
          }
        }
      })
      .catch((err) => {
        showErrorMessage(err);
      })
      .finally(() => {
        setIsLoading(false);
        setIsSending(false);
      });
  };
  const shownFile = useMemo(() => {
    if (selectedFile !== null && showFileNumber !== null) {
      return selectedFile[showFileNumber];
    }
    return null;
  }, [selectedFile, showFileNumber]);
  const handleBelegClicked = (beleg) => {
    setIsLoading(true);

    fetchFile(client, accessToken, beleg.key, beleg.betreff + "_" + beleg.key + beleg.dateiart)
      .then((file) => {
        openFile(file.name, file.content, file.type);
      })
      .catch((err) => {
        showErrorMessage(err);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };
  const changePagination = (e, value) => {
    setShowFileNumber(value - 1);
  };
  if (isLoading) {
    return <LoadingComponent path={location} />;
  } else {
    return (
      <div>
        <Grid container justifyContent="center">
          <Grid item xs={12} sm={8} md={6} lg={4}>
            {aufgabe && (
              <Alert severity="info">
                <AlertTitle align="left">
                  <Typography align="left" variant="caption" display="block">
                    {t("Verknüpfte") +
                      " " +
                      aufgabe?.ART_TEXT +
                      " - " +
                      t("Erstellt am") +
                      " " +
                      aufgabe?.BEGINN_DAT_TEXT +
                      " " +
                      t("von") +
                      " " +
                      aufgabe?.ERSTELLT_VON_NAME}
                  </Typography>
                </AlertTitle>
                <Typography align="left" variant="subtitle1">
                  {aufgabe?.BETREFF}
                </Typography>
                <Typography align="left" variant="caption" display="block" color="info.dark">
                  {aufgabe?.BEZEICHNUNG}
                </Typography>
              </Alert>
            )}
            {!ablagenameSelectOptions ||
              (ablagenameSelectOptions.length == 0 && (
                <Card className={classes.paper}>
                  <CardContent>
                    <Alert align="left" severity="warning">
                      <AlertTitle>
                        <strong>{t("Keine Belegarten zur Übermittlung freigeschalten")}</strong>
                      </AlertTitle>
                      {t("Ihre Personalabteilung hat keine Belegarten für die Übermittlung zugelassen.")}
                    </Alert>
                  </CardContent>
                </Card>
              ))}
            {ablagenameSelectOptions && ablagenameSelectOptions.length > 0 && (
              <Card className={classes.paper}>
                <CardContent>
                  <TextField
                    id="ablagename"
                    label={t("Art des Dokuments")}
                    select
                    fullWidth
                    error={fieldErrors.ablagename !== ""}
                    value={selectedAblagename}
                    onChange={handleAblagenameChange}
                    variant="outlined"
                    disabled={!!aufgabe && !!aufgabe.HDOK_KEY}
                    helperText={
                      fieldErrors.ablagename
                        ? fieldErrors.ablagename
                        : aufgabe && aufgabe.HDOK_KEY
                          ? t("Der Ablagename ist von der verknüpften Aufgabe vorgegeben.")
                          : ""
                    }
                  >
                    {ablagenameSelectOptions.map((ablagename) => (
                      <MenuItem key={ablagename.key} value={ablagename.key}>
                        <ListItemText primary={ablagename.name} secondary={ablagename.helperText} />
                      </MenuItem>
                    ))}
                  </TextField>
                  <Box p={1}>
                    <FileUploadButton
                      label={t("Datei auswählen")}
                      multiple={false}
                      enableCamera={true}
                      accept="application/pdf,image/png,image/jpeg,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                      onChange={onDateiChange}
                    />
                  </Box>

                  <Box p={1}>
                    {shownFile && isImage(shownFile) && <CardMedia component="img" title={shownFile.name} image={getDataUrl(shownFile)} />}

                    {!shownFile && (
                      <Typography color="textSecondary" className={classes.typo}>
                        {t("Keine Datei ausgewählt")}
                      </Typography>
                    )}

                    {shownFile && (
                      <Typography color="textSecondary" className={classes.typo}>
                        {shownFile.name}
                      </Typography>
                    )}
                  </Box>

                  {gueltigAbAnzeigen && (
                    <TextField
                      id="gueltigAb"
                      label={t("Gültig ab")}
                      type="date"
                      fullWidth
                      error={fieldErrors.gueltigAb !== ""}
                      helperText={fieldErrors.gueltigAb ? fieldErrors.gueltigAb : t("Beginn der Gültigkeit oder Datum der Ausstellung angeben.")}
                      value={gueltigAb}
                      onChange={handleGueltigAbChange}
                      variant="outlined"
                      sx={{ marginTop: 3 }}
                    />
                  )}

                  {gueltigBisAnzeigen && (
                    <TextField
                      id="gueltigBis"
                      label={t("Gültig bis")}
                      type="date"
                      fullWidth
                      error={fieldErrors.gueltigBis !== ""}
                      helperText={fieldErrors.gueltigBis ? fieldErrors.gueltigBis : t("Ende der Gültigkeit angeben.")}
                      value={gueltigBis}
                      onChange={handleGueltigBisChange}
                      variant="outlined"
                      sx={{ marginTop: 3 }}
                    />
                  )}

                  {selectedFile?.length > 1 && (
                    <Box
                      alignContent={"center"}
                      justifyContent={"center"}
                      sx={{
                        position: "relative",
                        display: "inline-flex",
                        alignContent: "center",
                        width: "100%",
                      }}
                    >
                      <Pagination count={selectedFile?.length} onChange={changePagination} />
                    </Box>
                  )}
                </CardContent>

                <CardActions disableSpacing>
                  {selectedFile?.length > 0 && (
                    <Box p={1}>
                      <FileUploadButton
                        label={t("Weitere Datei hinzufügen")}
                        multiple={false}
                        enableCamera={true}
                        accept="application/pdf,image/png,image/jpeg,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                        onChange={addDatei}
                      />
                    </Box>
                  )}

                  <Button
                    color="primary"
                    className={classes.sendButton}
                    disabled={isSending}
                    onClick={onSubmit}
                    endIcon={isSending ? <CircularProgress /> : null}
                  >
                    {t("Senden")}
                  </Button>
                </CardActions>
              </Card>
            )}
            <Card className={classes.paper}>
              <TransferHistory history={history} onBelegClicked={handleBelegClicked} hideSearchbar={true} />
            </Card>
          </Grid>
        </Grid>
      </div>
    );
  }
}

FileTransfer.propTypes = {
  classes: PropTypes.object.isRequired,
};
export default withStyles(styles)(FileTransfer);
